// THE MODEL ESCALATION LADDER (2026-09-24) — when a coding task's resumed red gate stops
// getting closer, its next attempt runs on a STRONGER model the account itself named. A
// second trigger (#6172): a stuck-PR RESCUE round — a babysit follow-up whose spec carries
// `rescue`, minted for an AI-managed PR parked past the rescue clock on `merge_conflict` or
// `ci_not_converging` (`entities/babysit/stuck-rescue.ts`) — escalates from its first
// attempt. Both are named on the audit row as a {@link ModelEscalationTrigger}.
// See `../llm-credential.ts` for the file docblock.
//
// THE ASK. tesote/tesote.ai runs its fleet on `glm-5.3-flash` (a z.ai coding-plan
// subscription). Large slices end red on their last few gate failures and burn attempts
// there. The ask was: after K attempts that made no progress on the saved WIP, spend the
// next one on a stronger model from the account's own pool, then drop back.
//
// THE TRIGGER is the runner's own measurement, never a re-derivation: `team_wip_saved`
// carries `gate_progress {from, to}` exactly when an attempt RESUMED a saved WIP and its
// gate went red again (`apps/runner/.../team/session/resume.ts`). `to >= from` is a stall.
// It is the complement of the progress ceiling (`entities/task/gate-progress.ts`), which
// buys ladder on `to < from` from the same two numbers — one signal, two arms.
//
// K IS ONE, and the reason is arithmetic, not taste. A `verify_failed` that did not get
// closer is charged against the kind's 3-attempt ladder: attempt 1 saves the WIP, attempt 2
// is the first resume that CAN report a stall, and a non-progress red gate at attempt 3
// exhausts the ladder. So a second consecutive stall is the ladder's last attempt: with
// K = 2 the escalated attempt would come only after the task had already run out. Measured
// on prod, 7 days to 2026-09-24 over every task with a `team_wip_saved` gate-red save: no
// task had two consecutive non-improving resumes. Since #4951 an exhausted task with saved
// work PARKS and resumes on a backoff instead of failing; with K = 1 those resume cycles
// run on the stronger model from the first one.
//
// STICKY WITHIN A TASK. Once a task has stalled, every later attempt of THAT task keeps
// escalating even after one gets closer — dropping back to the model that stalled mid-task
// throws away the attempt that was working. It ends when the task does; other tasks never
// read this task's history, so they run on the default from the start.
//
// NEVER A MODEL THE ACCOUNT DID NOT NAME (plan 102 slice 02 deleted the platform's
// `(provider, tier)` model guess, and this does not bring it back). Two rungs, in order:
//   1. an `llm_routes` row with purpose `escalation` (repo over org) — the account's
//      explicit answer, and the one that reaches "the same key's stronger model": a route
//      pin seals its `model` on the key it matched by provider or label even when the
//      catalogue lists only the cheap one, exactly as every other route pin does;
//   2. else a model the account TAGGED with a stronger tier (`byok_key_models.tier`, or the
//      key-level `tier` for the key's own model) than the one the task runs. An untagged
//      current model ranks as `general` — the everyday model — so a `fast` tag never
//      counts as stronger. This is what "on by default only when the pool has a stronger
//      model" means: the pool says so, or nothing escalates.
// Pure. Cooldowns, skip sets and the seal are the pool resolver's; this names the target.

import { type BYOKKey, BYOKKeyKind, type BYOKProvider, BYOKTier } from '../../entities/byok';
import { gateGotCloser } from '../../entities/task/gate-progress';
import type { StuckPrReason } from '../task-envelope';
import { ErrorPayloadSchema } from '../task-run-event/stream';
import { refuseKeyModel } from './models';
import type { ResolveLlmInput } from './pool-input';
import {
  activeModels,
  effectiveKeyKind,
  isUsable,
  keyPriority,
  visibleToActor,
} from './predicates';

/** Consecutive stalled resumes that trigger escalation — see the file header for why 1. */
export const MODEL_ESCALATION_STALL_STREAK = 1;

/** The stall that armed the ladder: the saved attempt's failures, this one's, and the run. */
export type GateStall = { readonly from: number; readonly to: number; readonly streak: number };

/**
 * What armed the ladder for one claim, as the `dispatch.model_escalated` row stores it
 * (`output.trigger`, #6172 — it replaced a bare `stall`, which only the reasoning string
 * read):
 *   - `gate_stall`: the task's resumed WIP gate stopped getting closer ({@link gateStall});
 *   - `stuck_pr`: a babysit RESCUE round (`envelope.babysit.rescue`) — the PR parked on
 *     `reason` after its head sat `parked_ms` without progress (the head's idle time since
 *     #6231; the park's age before it, the field keeping its stored name), and its one
 *     round runs here.
 * A stored shape: a renamed kind or field is a new one, never an edit.
 */
export type ModelEscalationTrigger =
  | ({ readonly kind: 'gate_stall' } & GateStall)
  | { readonly kind: 'stuck_pr'; readonly reason: StuckPrReason; readonly parked_ms: number }
  /**
   * #6767: a person sent the task to Escalation on one machine (`envelope.sent.role`), so
   * it escalates from its first attempt, not after a stall.
   */
  | { readonly kind: 'sent' };

const WIP_SAVED_EVENT = 'team_wip_saved';
const GateProgressSchema = ErrorPayloadSchema.shape.gate_progress;

type WipSave = {
  kind: string;
  progress: { from: number; to: number; stage?: 'advanced' | 'regressed' | undefined } | undefined;
};

/** One raw `team_wip_saved` status payload, or null when the row is not one. Box input. */
function wipSaveOf(raw: unknown): WipSave | null {
  if (raw === null || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  if (row['event'] !== WIP_SAVED_EVENT || typeof row['kind'] !== 'string') return null;
  const progress = GateProgressSchema.safeParse(row['gate_progress']);
  return { kind: row['kind'], progress: progress.success ? progress.data : undefined };
}

/**
 * Did this task's WIP stop getting closer? `saves` are the task's `team_wip_saved`
 * payloads NEWEST FIRST (raw — each is parsed here, and one that does not parse is
 * skipped, never thrown on). Null unless:
 *   - the NEWEST save left a WIP on the ref to finish — `kind: 'saved'` (it wrote one) or
 *     `kind: 'kept'` (it went red on MORE failures than the save already there, which it
 *     left standing, `apps/runner/.../team/session/wip.ts` `keptBetterSave`) — escalation is
 *     scoped to finishing that diff, and a failed/skipped save says nothing about the ref; and
 *   - somewhere in the task's history {@link MODEL_ESCALATION_STALL_STREAK} consecutive
 *     resumed gates did not get closer (`gateGotCloser`: `to >= from`, or a stage that
 *     regressed; a stage that advanced is closer whatever the counts). A save without `gate_progress`
 *     (a first attempt, an interrupted one) is neither and is skipped.
 * The newest qualifying stall is returned, for the audit row.
 */
export function gateStall(saves: readonly unknown[]): GateStall | null {
  const parsed = saves.map(wipSaveOf).filter((s): s is WipSave => s !== null);
  const newest = parsed[0]?.kind;
  if (newest !== 'saved' && newest !== 'kept') return null;
  let streak = 0;
  let hit: GateStall | null = null;
  for (const save of [...parsed].reverse()) {
    if (save.progress === undefined) continue;
    streak = gateGotCloser(save.progress) ? 0 : streak + 1;
    if (streak >= MODEL_ESCALATION_STALL_STREAK) {
      hit = { from: save.progress.from, to: save.progress.to, streak };
    }
  }
  return hit;
}

/** Which rung named the target — the account's explicit route, or its tier tags. */
export const EscalationVia = { Route: 'route', Tier: 'tier' } as const;
export type EscalationVia = (typeof EscalationVia)[keyof typeof EscalationVia];

/** Why a stalled task was NOT escalated — each one a sentence the audit row carries. */
export const EscalationSkip = {
  /** No `escalation` route, and nothing in the pool is tagged stronger than the current model. */
  NoStrongerModel: 'no_stronger_model',
  /** The `escalation` route names no usable pool key (wrong provider/label, fenced, parked). */
  RouteUnserved: 'route_unserved',
  /** The `escalation` route names the model the task already runs. */
  SameModel: 'same_model',
  /** The target key is METERED and the account is at 80%+ of the spend cap it set. */
  SpendCapped: 'spend_capped',
  /** The target key is METERED and the account's spend could not be read — fail closed. */
  SpendUnread: 'spend_unread',
  /** The plan named a target and the pool resolved around it (cooling, skipped). */
  Unserved: 'unserved',
  /** The default resolution sealed nothing, so there is no current model to climb from. */
  NoCurrentModel: 'no_current_model',
  /**
   * The escalated probe resolution THREW something other than a pool hold (a driver fault,
   * a bug). Only the IO shell observes it; distinct from {@link EscalationSkip.Unserved} (the
   * pool resolved around the target) so a broken probe is countable, not passed off as capacity.
   */
  ProbeFailed: 'probe_failed',
  /**
   * The claiming machine has its own Escalation switch OFF (#6758): an `agent_modes` row at
   * `scope = 'machine'` for that runner, read under the claim's fence. The claim seals the
   * base resolution, and no pool or route read is spent on a plan it will not use.
   */
  MachineRoleOff: 'machine_role_off',
  /**
   * The claiming machine's Escalation switch could not be read (#6758) — fail closed toward
   * the base model, the posture {@link EscalationSkip.SpendUnread} takes.
   */
  MachineSwitchUnread: 'machine_switch_unread',
} as const;
export type EscalationSkip = (typeof EscalationSkip)[keyof typeof EscalationSkip];

/** The route pin an escalated attempt resolves under — the same shape as `ResolveLlmInput.route`. */
export type EscalationPin = NonNullable<ResolveLlmInput['route']>;

export type EscalationTarget = {
  readonly via: EscalationVia;
  /**
   * Fed to the pool resolver AS the purpose route, in place of the task's own. A route is
   * the one rung that seals its `model` on a key matched by provider/label even when the
   * catalogue does not list it, which is what makes "the same key's stronger model" work.
   */
  readonly pin: EscalationPin;
  /** The pool key the plan expects to serve it (best-ranked candidate). */
  readonly keyId: string;
  /** The model the escalated attempt runs, or null when the route names no model. */
  readonly model: string | null;
  /** Does the target key meter — the one case a spend cap is consulted for. */
  readonly metered: boolean;
  /** The tag that made it stronger (`tier` rung), or null (`route` rung). */
  readonly tier: BYOKTier | null;
};

export type EscalationPlan =
  | { readonly kind: 'escalate'; readonly target: EscalationTarget }
  | { readonly kind: 'skip'; readonly reason: EscalationSkip };

/** Cheapest → strongest, the order `BYOKTier`'s own docblock states. */
const TIER_RANK: Readonly<Record<BYOKTier, number>> = {
  [BYOKTier.Fast]: 0,
  [BYOKTier.General]: 1,
  [BYOKTier.Smart]: 2,
  [BYOKTier.Heavy]: 3,
};
/** An untagged model is the account's everyday model — see the file header. */
const UNTAGGED_RANK = TIER_RANK[BYOKTier.General];

/** The facts the pool resolver gates usability on — the same ones, so the two agree. */
export type EscalationPoolFacts = Pick<
  ResolveLlmInput,
  'keys' | 'actingUserId' | 'ossEligible' | 'taskPurpose' | 'boxOwnerKind'
>;

/** The pool's usable keys, by the resolver's own gates (cooldown aside: the seal decides that). */
function usableKeys(pool: EscalationPoolFacts): BYOKKey[] {
  return pool.keys.filter(
    (k) =>
      isUsable(k, pool.ossEligible ?? false, false, pool.boxOwnerKind, pool.taskPurpose) &&
      visibleToActor(k, pool.actingUserId),
  );
}

const subscriptionFirst = (k: BYOKKey): number =>
  effectiveKeyKind(k) === BYOKKeyKind.Subscription ? 0 : 1;

/**
 * The order among candidates the pool would treat alike: SUBSCRIPTION before metered, then
 * the account's `priority`, then id. Subscription leads here, where the pool resolver puts
 * priority first, on purpose: an escalation is extra spend on top of the attempt the account
 * already paid for, so a prepaid token is spent before a metered one whatever its rank.
 */
function bySpendThenPreference(a: BYOKKey, b: BYOKKey): number {
  if (subscriptionFirst(a) !== subscriptionFirst(b))
    return subscriptionFirst(a) - subscriptionFirst(b);
  if (keyPriority(a) !== keyPriority(b)) return keyPriority(a) - keyPriority(b);
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Would this route pin land on this key — every axis the pin sets, matched. */
function routeServes(key: BYOKKey, pin: EscalationPin): boolean {
  if (pin.key_label !== undefined && key.label !== pin.key_label) return false;
  if (pin.provider !== undefined && key.provider !== pin.provider) return false;
  const model = pin.model ?? key.model;
  if (refuseKeyModel(key, model) !== null) return false;
  // A model-only pin has no provider/label to match a key by: it lands only where the
  // key can SERVE the model, the resolver's own reading of a model-only route.
  if (pin.key_label === undefined && pin.provider === undefined) {
    return key.model === model || activeModels(key).some((m) => m.model === model);
  }
  return true;
}

function planFromRoute(
  usable: readonly BYOKKey[],
  current: { keyId: string; model: string },
  pin: EscalationPin,
): EscalationPlan {
  const best = usable.filter((k) => routeServes(k, pin)).sort(bySpendThenPreference)[0];
  if (best === undefined) return { kind: 'skip', reason: EscalationSkip.RouteUnserved };
  if ((pin.model ?? best.model) === current.model) {
    return { kind: 'skip', reason: EscalationSkip.SameModel };
  }
  return {
    kind: 'escalate',
    target: {
      via: EscalationVia.Route,
      pin,
      keyId: best.id,
      model: pin.model ?? null,
      metered: subscriptionFirst(best) === 1,
      tier: null,
    },
  };
}

/** Every `(model, tier)` a key serves with a tag — its live catalogue, plus its own model under the key-level tag. */
function taggedModels(key: BYOKKey): { model: string; tier: BYOKTier }[] {
  const out = new Map<string, BYOKTier>();
  if (key.tier && refuseKeyModel(key, key.model) === null) out.set(key.model, key.tier);
  for (const m of activeModels(key)) if (m.tier) out.set(m.model, m.tier);
  return [...out].map(([model, tier]) => ({ model, tier }));
}

/** The rank the task runs at now: the current model's tag on its key, else `general`. */
function currentRank(keys: readonly BYOKKey[], current: { keyId: string; model: string }): number {
  const key = keys.find((k) => k.id === current.keyId);
  const tag = key ? taggedModels(key).find((t) => t.model === current.model)?.tier : undefined;
  return tag ? TIER_RANK[tag] : UNTAGGED_RANK;
}

function planFromTiers(
  pool: EscalationPoolFacts,
  usable: readonly BYOKKey[],
  current: { keyId: string; model: string },
): EscalationPlan {
  const floor = currentRank(pool.keys, current);
  const candidates = usable.flatMap((key) =>
    taggedModels(key)
      .filter((t) => TIER_RANK[t.tier] > floor && t.model !== current.model)
      .map((t) => ({ key, ...t })),
  );
  candidates.sort(
    (a, b) =>
      TIER_RANK[b.tier] - TIER_RANK[a.tier] ||
      bySpendThenPreference(a.key, b.key) ||
      (a.model < b.model ? -1 : a.model > b.model ? 1 : 0),
  );
  const best = candidates[0];
  if (best === undefined) return { kind: 'skip', reason: EscalationSkip.NoStrongerModel };
  return {
    kind: 'escalate',
    target: {
      via: EscalationVia.Tier,
      pin: {
        provider: best.key.provider as BYOKProvider,
        model: best.model,
        ...(best.key.label ? { key_label: best.key.label } : {}),
      },
      keyId: best.key.id,
      model: best.model,
      metered: subscriptionFirst(best.key) === 1,
      tier: best.tier,
    },
  };
}

/**
 * Name the stronger `(key, model)` a stalled task climbs to, or why it does not. `route` is
 * the account's `escalation` route pin (repo over org), undefined when it set none; `current`
 * is what the task's default resolution sealed. Every candidate passes the pool resolver's
 * own usability and visibility gates, so a fenced, donated-on-private, another member's or
 * revoked key is never a target. The spend cap is the caller's (it needs a read).
 */
export function planModelEscalation(input: {
  pool: EscalationPoolFacts;
  current: { keyId: string; model: string };
  route: EscalationPin | undefined;
}): EscalationPlan {
  const usable = usableKeys(input.pool);
  return input.route !== undefined
    ? planFromRoute(usable, input.current, input.route)
    : planFromTiers(input.pool, usable, input.current);
}

/**
 * Did the escalated resolution actually seal the target? The PLAN is a prediction; the
 * sealed credential is the fact. It landed when the route decided the pick
 * (`resolved_by: 'route'`), the model moved off the one the task was running, and — when the
 * plan named a model — it is that model. Anything else (the target key cooling or in the
 * task's skip set, so the pool ranked around the pin) is {@link EscalationSkip.Unserved}.
 */
export function escalatedResolutionLanded(
  target: Pick<EscalationTarget, 'model'>,
  sealed: { model: string; resolved_by?: string | undefined },
  currentModel: string,
): boolean {
  return (
    sealed.resolved_by === 'route' &&
    sealed.model !== currentModel &&
    (target.model === null || sealed.model === target.model)
  );
}
